import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePropertyFilter } from '@/utils/usePropertyFilter';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

const SolarProjectInput = () => {
    const { apiFetch } = useAuth();
    const { filterData, isLoading: filterLoading } = usePropertyFilter();
    const [allProperties, setAllProperties] = useState([]);
    const [selectedPropertyID, setSelectedPropertyID] = useState('');
    const [form, setForm] = useState({
        plantSize: '',
        yield: '',
        expectedEnergyGeneration: '',
        installationCompleted: ''
    });
    const [saving, setSaving] = useState(false);

    // Fetch all properties
    useEffect(() => {
        const fetchAllProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && data.data) {
                    // Filter properties by user's access
                    const filteredProperties = filterData(data.data, 'propertyID');
                    setAllProperties(filteredProperties);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                toast.error('Failed to load properties');
            }
        };
        
        // Wait for property filter to load before fetching properties
        if (!filterLoading) {
            fetchAllProperties();
        }
    }, [filterLoading]);

    // Fetch solar project stats for selected property
    useEffect(() => {
        if (!selectedPropertyID) {
            setForm({
                plantSize: '',
                yield: '',
                expectedEnergyGeneration: '',
                installationCompleted: ''
            });
            return;
        }
        const fetchSolarStats = async () => {
            try {
                const response = await apiFetch(`/api/Ops/solar-project-stats/${selectedPropertyID}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data && data.id) {
                        setForm({
                            plantSize: data.plantSize ?? '',
                            yield: data.yield ?? '',
                            expectedEnergyGeneration: data.expectedEnergyGeneration ?? '',
                            installationCompleted: data.installationCompleted
                                ? data.installationCompleted.split('T')[0]
                                : ''
                        });
                    } else {
                        setForm({
                            plantSize: '',
                            yield: '',
                            expectedEnergyGeneration: '',
                            installationCompleted: ''
                        });
                    }
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                toast.error('Failed to load solar project stats');
            }
        };
        fetchSolarStats();
    }, [selectedPropertyID]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!selectedPropertyID) {
            toast.error('Please select a property');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                propertyID: Number(selectedPropertyID),
                plantSize: Number(form.plantSize) || 0,
                yield: Number(form.yield) || 0,
                expectedEnergyGeneration: Number(form.expectedEnergyGeneration) || 0,
                installationCompleted: form.installationCompleted
            };
            const response = await apiFetch('/api/Ops/solar-project-stats/upsert', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                toast.success('Solar project stats saved!');
            } else {
                toast.error('Save failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ maxWidth: 500, margin: '0 auto', padding: 24 }}>
            <h2>Solar Project Input</h2>
            <form
                onSubmit={e => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="property">Property</label>
                    <select
                        id="property"
                        value={selectedPropertyID}
                        onChange={e => setSelectedPropertyID(e.target.value)}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                        required
                    >
                        <option value="">Select a property...</option>
                        {allProperties.map(p => (
                            <option key={p.propertyID} value={p.propertyID}>
                                {p.propertyName}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="plantSize">Plant Size (kW)</label>
                    <input
                        id="plantSize"
                        type="number"
                        min="0"
                        value={form.plantSize}
                        onChange={e => handleChange('plantSize', e.target.value)}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                        required
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="yield">Yield (kWh/kW)</label>
                    <input
                        id="yield"
                        type="number"
                        min="0"
                        value={form.yield}
                        onChange={e => handleChange('yield', e.target.value)}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                        required
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="expectedEnergyGeneration">Expected Energy Generation per year (kWh)</label>
                    <input
                        id="expectedEnergyGeneration"
                        type="number"
                        min="0"
                        value={form.expectedEnergyGeneration}
                        onChange={e => handleChange('expectedEnergyGeneration', e.target.value)}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                        required
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="installationCompleted">Installation Completed Date</label>
                    <input
                        id="installationCompleted"
                        type="date"
                        value={form.installationCompleted}
                        onChange={e => handleChange('installationCompleted', e.target.value)}
                        style={{ width: '100%', padding: 8, marginTop: 4 }}
                        required
                    />
                </div>
                <button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </form>
</div>
    );
};

export default SolarProjectInput;